/* Banking Portal · Tier-1 Program — Waves 13/14/15 (Feb 2026).
   Three pages in one file because they share helper components.

   Wave-15 · AML Typology Coverage      /banking/aml-typologies
   Wave-14 · Policy Decision Trace      /banking/policy-trace/:txnId
   Wave-13 · Alert Subscriptions        /banking/alerts
*/
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, FileSearch, Bell, CheckCircle2, AlertTriangle, XCircle, Eye,
  Activity, Webhook, Mail, MessageSquare, Filter, Send, Trash2, Plus,
  ChevronRight, Building2, ListChecks, Layers, Sparkles, Clock,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

function fmt(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}
function money(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function ts(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function ago(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const SEVERITY_CLS = {
  critical:     "bg-rose-50 text-rose-700 border-rose-200",
  high:         "bg-amber-50 text-amber-700 border-amber-200",
  medium:       "bg-sky-50 text-sky-700 border-sky-200",
  informational: "bg-slate-50 text-slate-600 border-slate-200",
};

// ════════════════════════════════════════════════════════════════════════
// Wave-15 · AML TYPOLOGY COVERAGE
// ════════════════════════════════════════════════════════════════════════
export function BankingAmlTypologies() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/partner/banking/aml/typology-coverage?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load AML typology coverage"));
  }, [days]);

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="banking-aml-typologies">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-slate-500" /> AML Typology Coverage
          </h1>
          <Badge variant="outline" className="text-[10px] bg-slate-50">FFIEC BSA/AML aligned</Badge>
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">engine {data.engine_version}</Badge>
        </div>
        <p className="text-[13px] text-slate-500 mt-1">
          16 detection typologies from the FFIEC BSA/AML Examination Manual. Live counts show recent fires in the selected window.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="typology-kpis">
        <Kpi label="Total typologies" value={fmt(data.typology_count)} icon={ListChecks} />
        <Kpi label="Active rules" value={fmt(data.active_count)} tone="emerald" icon={CheckCircle2} />
        <Kpi label="Monitoring only" value={fmt(data.monitoring_only)} tone="amber" icon={Eye} />
        <Kpi label="Fired in window" value={fmt(data.active_fired_count)} tone={data.active_fired_count > 0 ? "amber" : "emerald"} icon={Activity} />
      </div>

      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="typology-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Typology</th>
              <th className="text-left px-3 py-2 font-medium">FFIEC ref</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Severity</th>
              <th className="text-right px-3 py-2 font-medium">Fired</th>
              <th className="text-right px-3 py-2 font-medium">Users</th>
              <th className="text-left px-3 py-2 font-medium">Last fired</th>
            </tr>
          </thead>
          <tbody data-testid="typology-rows">
            {data.rows.map(r => (
              <tr key={r.kind} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5">
                  <div className="text-[12.5px] text-slate-900 font-medium">{r.title}</div>
                  <div className="text-[10.5px] text-slate-500 max-w-md">{r.description}</div>
                </td>
                <td className="px-3 py-2.5 text-[11px] text-slate-600">{r.ffiec_section}</td>
                <td className="px-3 py-2.5">
                  {r.status === "active"
                    ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]">active</Badge>
                    : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10.5px]" title={r.gap_reason}>monitoring only</Badge>}
                </td>
                <td className="px-3 py-2.5">
                  <Badge variant="outline" className={`${SEVERITY_CLS[r.severity] || SEVERITY_CLS.informational} text-[10.5px]`}>{r.severity}</Badge>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[13px]">
                  <span className={r.fired_count > 0 ? "text-amber-700 font-medium" : "text-slate-400"}>
                    {fmt(r.fired_count)}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[13px] text-slate-700">{fmt(r.users_flagged)}</td>
                <td className="px-3 py-2.5 text-[11px] text-slate-500">{r.last_fired_at ? ago(r.last_fired_at) + " ago" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-slate-500 italic">
        {data.ffiec_alignment} · engine {data.engine_version} · {data.monitoring_only > 0 ? "1 typology disclosed as monitoring-only (data source not yet captured) — honest gap, not a hidden one." : ""}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Wave-14 · POLICY DECISION TRACE
// ════════════════════════════════════════════════════════════════════════
const TRACE_STATUS_TONE = {
  passed:           { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  warning:          { cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: AlertTriangle },
  failed:           { cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: XCircle },
  exceeded:         { cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: XCircle },
  blocked:          { cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: XCircle },
  alerts_fired:     { cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: AlertTriangle },
  override_applied: { cls: "bg-violet-50 text-violet-700 border-violet-200",    icon: Sparkles },
  no_override:      { cls: "bg-slate-50 text-slate-600 border-slate-200",       icon: CheckCircle2 },
  informational:    { cls: "bg-slate-50 text-slate-600 border-slate-200",       icon: Eye },
};

export function BankingPolicyTrace() {
  const { txnId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [searchId, setSearchId] = useState(txnId || "");

  useEffect(() => {
    if (!txnId) return;
    api.get(`/partner/banking/policy-trace/${txnId}`)
      .then(r => setData(r.data))
      .catch(e => {
        if (e?.response?.status === 404) toast.error("Transaction not found");
        else toast.error("Could not load trace");
        setData({ error: true });
      });
  }, [txnId]);

  const decisionTone = data?.final_decision === "approved"
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : data?.final_decision === "approved_with_warnings"
      ? "bg-amber-50 border-amber-200 text-amber-700"
      : "bg-rose-50 border-rose-200 text-rose-700";

  if (!txnId) {
    return (
      <div className="space-y-5" data-testid="policy-trace-search">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-slate-500" /> Policy Decision Trace
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Enter any wallet-transaction ID to see the rules-engine evaluation Verqo performed — every check, every signal, every decision.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 max-w-2xl">
          <Label className="text-xs">Transaction ID</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              value={searchId}
              onChange={e => setSearchId(e.target.value)}
              placeholder="wallet_txn id…"
              data-testid="policy-trace-id-input"
              className="text-sm font-mono"
            />
            <Button
              onClick={() => searchId && nav(`/banking/policy-trace/${searchId}`)}
              data-testid="policy-trace-go-btn"
            >Go</Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            You can also deep-link from any row in <Link to="/banking/forensics" className="text-sky-600 hover:underline">Forensics → Transactions</Link>.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="text-sm text-slate-400">Loading trace…</div>;
  if (data.error) return <div className="text-sm text-rose-500">Transaction not found.</div>;

  return (
    <div className="space-y-5" data-testid="banking-policy-trace">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => nav("/banking/policy-trace")} className="text-xs h-7 px-2" data-testid="trace-back-btn">← Search</Button>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-slate-500" /> Policy Trace
          </h1>
          <Badge variant="outline" className="text-[10px] font-mono">{data.txn.id}</Badge>
        </div>
      </div>

      {/* Final decision badge */}
      <div className={`rounded-xl border-2 px-5 py-4 ${decisionTone}`} data-testid="trace-decision">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider opacity-75">Final decision</div>
            <div className="text-2xl font-bold capitalize mt-0.5">{(data.final_decision || "").replace(/_/g, " ")}</div>
          </div>
          <div className="flex gap-4 text-center">
            <div><div className="text-2xl font-bold tabular-nums">{data.summary.passed}</div><div className="text-[10.5px] opacity-75">passed</div></div>
            <div><div className="text-2xl font-bold tabular-nums">{data.summary.warnings}</div><div className="text-[10.5px] opacity-75">warnings</div></div>
            <div><div className="text-2xl font-bold tabular-nums">{data.summary.failed}</div><div className="text-[10.5px] opacity-75">failed</div></div>
            <div><div className="text-2xl font-bold tabular-nums">{data.summary.informational}</div><div className="text-[10.5px] opacity-75">info</div></div>
          </div>
        </div>
      </div>

      {/* Transaction context */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Transaction</h3>
          <dl className="grid grid-cols-2 gap-y-1.5 text-[12.5px]">
            <dt className="text-slate-500">Type</dt><dd className="text-slate-900 capitalize">{data.txn.type}</dd>
            <dt className="text-slate-500">Direction</dt><dd className="text-slate-900 capitalize">{data.txn.direction}</dd>
            <dt className="text-slate-500">Amount</dt><dd className="text-slate-900 tabular-nums">{money(Math.abs(data.txn.amount || 0))}</dd>
            <dt className="text-slate-500">Method</dt><dd className="text-slate-900">{data.txn.method || "—"}</dd>
            <dt className="text-slate-500">Status</dt><dd className="text-slate-900">{data.txn.status}</dd>
            <dt className="text-slate-500">Ref</dt><dd className="text-slate-900 font-mono">{data.txn.ref || "—"}</dd>
            <dt className="text-slate-500">When</dt><dd className="text-slate-900">{ts(data.txn.created_at)}</dd>
          </dl>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> Party</h3>
          <dl className="grid grid-cols-2 gap-y-1.5 text-[12.5px]">
            <dt className="text-slate-500">Company</dt><dd className="text-slate-900">{data.party?.company || "—"}</dd>
            <dt className="text-slate-500">Role</dt><dd className="text-slate-900 capitalize">{data.party?.role || "—"}</dd>
            <dt className="text-slate-500">Email</dt><dd className="text-slate-900 font-mono">{data.party?.email_masked || "—"}</dd>
            <dt className="text-slate-500">User id</dt><dd className="text-slate-900 font-mono text-[10.5px]">{data.party?.id || "—"}</dd>
          </dl>
          {data.party?.id && (
            <Link to={`/banking/customer-profile/${data.party.id}`} className="mt-3 inline-flex text-[11px] text-sky-600 hover:underline items-center gap-0.5">
              View customer profile <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      {/* Trace timeline */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="trace-timeline">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Rules-engine evaluation</h3>
          <div className="text-[10.5px] text-slate-500 tabular-nums">{data.summary.total_checks} checks</div>
        </div>
        <div className="divide-y divide-slate-100">
          {data.trace.map((t, i) => {
            const tone = TRACE_STATUS_TONE[t.status] || TRACE_STATUS_TONE.informational;
            const Icon = tone.icon;
            return (
              <div key={i} className="px-4 py-3 flex items-start gap-3" data-testid={`trace-step-${t.step}`}>
                <div className="text-[11px] tabular-nums text-slate-400 mt-0.5 w-6">{t.order}.</div>
                <Icon className={`w-4 h-4 mt-0.5 ${
                  t.status === "passed"        ? "text-emerald-500"
                  : t.status === "warning"      ? "text-amber-500"
                  : t.status === "alerts_fired" ? "text-amber-500"
                  : t.status === "override_applied" ? "text-violet-500"
                  : ["failed", "exceeded", "blocked"].includes(t.status) ? "text-rose-500"
                  : "text-slate-400"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-medium text-slate-900 capitalize">{t.step.replace(/_/g, " ")}</div>
                    <Badge variant="outline" className={`${tone.cls} text-[10px] capitalize`}>{(t.status || "").replace(/_/g, " ")}</Badge>
                  </div>
                  <div className="text-[11.5px] text-slate-600 mt-0.5">{t.signal}</div>
                  {Array.isArray(t.detail) && t.detail.length > 0 && (
                    <div className="mt-1.5 space-y-1">
                      {t.detail.slice(0, 3).map((d, j) => (
                        <div key={j} className="text-[10.5px] text-slate-500 font-mono">
                          {Object.entries(d).map(([k, v]) => `${k}=${v}`).join(" · ")}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Attestation footer */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-[11px] text-slate-500 italic" data-testid="trace-attestation">
        {data.attestation}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Wave-13 · ALERT SUBSCRIPTIONS
// ════════════════════════════════════════════════════════════════════════
function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const cls = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const CHANNEL_ICON = { email: Mail, webhook: Webhook, sms: MessageSquare };

function NewSubscriptionDialog({ open, onClose, catalog, onCreated }) {
  const [form, setForm] = useState({
    label: "",
    event_types: [],
    severity_threshold: "medium",
    channels: ["email"],
    webhook_url: "",
    email_recipient: "",
    sms_recipient: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/partner/banking/alerts/subscriptions", form);
      toast.success("Subscription created");
      onCreated(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || "Could not create subscription");
    } finally { setBusy(false); }
  };

  const toggleEvent = k => setForm(f => ({
    ...f,
    event_types: f.event_types.includes(k)
      ? f.event_types.filter(x => x !== k)
      : [...f.event_types, k],
  }));

  const toggleChannel = c => setForm(f => ({
    ...f,
    channels: f.channels.includes(c)
      ? f.channels.filter(x => x !== c)
      : [...f.channels, c],
  }));

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-sub-dialog">
        <DialogHeader>
          <DialogTitle>New alert subscription</DialogTitle>
          <DialogDescription>
            Pick which events your team should be notified about and the channels for delivery.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Label</Label>
            <Input
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. AML critical to compliance@bank"
              data-testid="sub-label"
              className="mt-1.5 text-sm"
            />
          </div>

          <div>
            <Label className="text-xs">Events ({form.event_types.length} selected)</Label>
            <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-60 overflow-y-auto border border-slate-200 rounded-md p-2">
              {catalog?.events?.map(e => (
                <label key={e.key} className="flex items-start gap-2 text-[12px] cursor-pointer hover:bg-slate-50 p-1.5 rounded">
                  <input
                    type="checkbox"
                    checked={form.event_types.includes(e.key)}
                    onChange={() => toggleEvent(e.key)}
                    data-testid={`event-${e.key}`}
                  />
                  <div>
                    <div className="text-slate-900">{e.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{e.key}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Min severity</Label>
              <Select value={form.severity_threshold} onValueChange={v => setForm({ ...form, severity_threshold: v })}>
                <SelectTrigger className="mt-1.5 h-9 text-xs" data-testid="sub-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catalog?.severities?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Channels</Label>
              <div className="mt-1.5 flex gap-2">
                {catalog?.channels?.map(c => {
                  const CIcon = CHANNEL_ICON[c];
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleChannel(c)}
                      data-testid={`channel-${c}`}
                      className={`flex-1 rounded-md border px-2 py-1.5 text-[11px] capitalize ${
                        form.channels.includes(c)
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {CIcon && <CIcon className="w-3 h-3 inline mr-1" />}{c}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {form.channels.includes("email") && (
            <div>
              <Label className="text-xs">Email recipient</Label>
              <Input value={form.email_recipient}
                     onChange={e => setForm({ ...form, email_recipient: e.target.value })}
                     placeholder="compliance@bank.example"
                     data-testid="sub-email"
                     className="mt-1.5 text-sm" />
            </div>
          )}
          {form.channels.includes("webhook") && (
            <div>
              <Label className="text-xs">Webhook URL</Label>
              <Input value={form.webhook_url}
                     onChange={e => setForm({ ...form, webhook_url: e.target.value })}
                     placeholder="https://bank.example/incoming/verqo"
                     data-testid="sub-webhook"
                     className="mt-1.5 text-sm font-mono" />
            </div>
          )}
          {form.channels.includes("sms") && (
            <div>
              <Label className="text-xs">SMS recipient</Label>
              <Input value={form.sms_recipient}
                     onChange={e => setForm({ ...form, sms_recipient: e.target.value })}
                     placeholder="+1 555 0100"
                     data-testid="sub-sms"
                     className="mt-1.5 text-sm font-mono" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit}
                  disabled={busy || form.event_types.length === 0 || form.channels.length === 0}
                  data-testid="sub-create-btn">
            {busy ? "Creating…" : "Create subscription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionsTab() {
  const [subs, setSubs] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const r = await api.get("/partner/banking/alerts/subscriptions");
    setSubs(r.data.rows || []);
  };

  useEffect(() => {
    load();
    api.get("/partner/banking/alerts/event-catalog").then(r => setCatalog(r.data));
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this subscription?")) return;
    await api.delete(`/partner/banking/alerts/subscriptions/${id}`);
    toast.success("Deleted");
    load();
  };

  const fireTest = async (sub) => {
    const evt = sub.event_types?.[0] || "aml.critical";
    try {
      await api.post("/partner/banking/alerts/test", {
        subscription_id: sub.id,
        event_type: evt,
        severity: "high",
        test_message: "Synthetic test event from portal",
      });
      toast.success("Test event fired");
    } catch {
      toast.error("Test fire failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="subscriptions-tab">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-slate-500">
          {subs.length === 0
            ? "No subscriptions yet. Create one so your team gets notified the moment something changes."
            : `${subs.length} subscription${subs.length === 1 ? "" : "s"} active.`}
        </p>
        <Button onClick={() => setShowNew(true)} size="sm" data-testid="new-sub-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New subscription
        </Button>
      </div>

      <div className="space-y-2">
        {subs.map(s => (
          <div key={s.id} data-testid={`sub-row-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[13.5px] font-medium text-slate-900">{s.label || "Alert subscription"}</div>
                {s.active
                  ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">active</Badge>
                  : <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-[10px]">paused</Badge>}
                <Badge variant="outline" className={`${SEVERITY_CLS[s.severity_threshold] || SEVERITY_CLS.medium} text-[10px]`}>
                  ≥ {s.severity_threshold}
                </Badge>
              </div>
              <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-1.5">
                {(s.event_types || []).map(e => (
                  <Badge key={e} variant="outline" className="text-[9.5px] font-mono bg-slate-50">{e}</Badge>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-2">
                {(s.channels || []).map(c => {
                  const CIcon = CHANNEL_ICON[c];
                  return CIcon ? <span key={c} className="inline-flex items-center gap-0.5"><CIcon className="w-3 h-3" /> {c}</span> : c;
                })}
                {s.email_recipient && <span className="text-slate-400">· {s.email_recipient}</span>}
                {s.webhook_url && <span className="text-slate-400 font-mono">· {s.webhook_url}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button variant="outline" size="sm" onClick={() => fireTest(s)} data-testid={`sub-test-${s.id}`}>
                <Send className="w-3 h-3 mr-1" /> Test
              </Button>
              <Button variant="ghost" size="sm" onClick={() => del(s.id)} data-testid={`sub-del-${s.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <NewSubscriptionDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        catalog={catalog}
        onCreated={() => load()}
      />
    </div>
  );
}

function DeliveriesTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    api.get(`/partner/banking/alerts/deliveries?days=${days}`)
      .then(r => setData(r.data));
  }, [days]);

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="deliveries-tab">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-[11.5px] text-slate-500 tabular-nums">
          {data.count} dispatched
        </span>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">When</th>
              <th className="text-left px-3 py-2 font-medium">Event</th>
              <th className="text-left px-3 py-2 font-medium">Severity</th>
              <th className="text-left px-3 py-2 font-medium">Channel</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Test?</th>
            </tr>
          </thead>
          <tbody data-testid="deliveries-rows">
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 italic">No deliveries yet. Fire a test event from a subscription to verify your handler.</td></tr>
            )}
            {data.rows.map(d => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2.5 text-[11.5px] text-slate-500">{ago(d.attempted_at)} ago</td>
                <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10.5px] font-mono bg-slate-50">{d.event_type}</Badge></td>
                <td className="px-3 py-2.5"><Badge variant="outline" className={`${SEVERITY_CLS[d.severity] || SEVERITY_CLS.informational} text-[10.5px]`}>{d.severity}</Badge></td>
                <td className="px-3 py-2.5 text-[11.5px] capitalize">{d.channel}</td>
                <td className="px-3 py-2.5">
                  <Badge variant="outline" className={
                    d.status === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10.5px]"
                    : d.status === "failed"  ? "bg-rose-50 text-rose-700 border-rose-200 text-[10.5px]"
                    : "bg-slate-50 text-slate-600 border-slate-200 text-[10.5px]"
                  }>{d.status}</Badge>
                </td>
                <td className="px-3 py-2.5 text-[10.5px] text-slate-500">{d.is_test ? "test" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DigestTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/partner/banking/alerts/digest/preview").then(r => setData(r.data));
  }, []);

  if (!data) return <div className="text-sm text-slate-400">Loading digest preview…</div>;

  return (
    <div className="space-y-4" data-testid="digest-tab">
      <div className="rounded-xl border-2 border-sky-200 bg-sky-50 p-5">
        <div className="flex items-center gap-2 text-sky-700">
          <Clock className="w-4 h-4" />
          <div className="text-sm font-semibold">Daily Compliance Digest · preview</div>
        </div>
        <p className="text-[12.5px] text-sky-700 mt-1.5">
          {data.summary_line}
        </p>
        <p className="text-[11px] text-sky-700 mt-2 opacity-80 italic">{data._note}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="New AML · 24h"    value={fmt(data.new_aml_24h)}             tone={data.new_aml_24h > 0 ? "amber" : "emerald"} icon={ShieldAlert} />
        <Kpi label="Open AML total"   value={fmt(data.open_aml_total)}          tone={data.open_aml_total > 0 ? "amber" : "emerald"} icon={Activity} />
        <Kpi label="Sanctions hits 24h" value={fmt(data.new_sanctions_24h)}       tone={data.new_sanctions_24h > 0 ? "rose" : "emerald"} icon={AlertTriangle} />
        <Kpi label="AAN 24h"          value={fmt(data.new_aan_24h)} icon={Mail} />
        <Kpi label="Elevated to T3"   value={fmt(data.new_high_risk_customers)} tone={data.new_high_risk_customers > 0 ? "amber" : "emerald"} icon={Layers} />
      </div>

      {data.top_alerts.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Top alerts (24h)</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {data.top_alerts.map(a => (
              <div key={a.id} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <div className="text-[13px] text-slate-900">{a.party?.company || "—"}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono">{a.party?.email_masked || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10.5px] font-mono">{a.kind}</Badge>
                  <Badge variant="outline" className={`${SEVERITY_CLS[a.severity] || SEVERITY_CLS.informational} text-[10.5px]`}>{a.severity}</Badge>
                  <span className="text-[10.5px] text-slate-500">{ago(a.triggered_at)} ago</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function BankingAlerts() {
  return (
    <div className="space-y-5" data-testid="banking-alerts">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-slate-500" /> Push Alerts & Digest
          </h1>
          <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">real-time · proactive</Badge>
        </div>
        <p className="text-[13px] text-slate-500 mt-1">
          Configure what your team gets notified about and how. Real-time push channels (email · webhook · SMS) plus a daily 07:00 UTC digest.
        </p>
      </div>

      <Tabs defaultValue="subscriptions" className="w-full">
        <TabsList data-testid="alerts-tabs">
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="deliveries"    data-testid="tab-deliveries">Recent Deliveries</TabsTrigger>
          <TabsTrigger value="digest"        data-testid="tab-digest">Daily Digest Preview</TabsTrigger>
        </TabsList>
        <TabsContent value="subscriptions" className="mt-4"><SubscriptionsTab /></TabsContent>
        <TabsContent value="deliveries"    className="mt-4"><DeliveriesTab /></TabsContent>
        <TabsContent value="digest"        className="mt-4"><DigestTab /></TabsContent>
      </Tabs>
    </div>
  );
}
